import { ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "./ui/sonner";
import { alignFranchise, fetchMe, formatApiError, getCachedUser } from "../utils/auth";
import { riotballTeams } from "../data/riotballTeams";

/* PICK YOUR FRANCHISE — align your Titan Tag with one of the ten tour teams.
   The choice is stored on the account and shown beside the runner's name on
   the homepage roster. Switching allegiance is free, always. */
export function FranchisePicker() {
  const [user, setUser] = useState(() => getCachedUser());
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    fetchMe().then(setUser).catch(() => {});
    const onAuth = (e) => setUser(e.detail?.user ?? null);
    window.addEventListener("dolo-auth", onAuth);
    return () => window.removeEventListener("dolo-auth", onAuth);
  }, []);

  const aligned = user?.franchise || null;

  const pick = async (team) => {
    if (!user) {
      toast.error("SIGN IN TO ALIGN — your Titan Tag needs a vault account.");
      return;
    }
    if (saving || aligned === team.id) return;
    setSaving(team.id);
    try {
      await alignFranchise(team.id);
      toast.success(`ALIGNED — ${team.name}. Your runner roster row now carries the crest.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "ALIGNMENT FAILED — RETRY."));
    } finally {
      setSaving(null);
    }
  };

  const alignedTeam = aligned ? riotballTeams.find((t) => t.id === aligned) : null;

  return (
    <section className="franchise-picker" data-testid="franchise-picker">
      <div className="franchise-picker-head">
        <span className="eyebrow">TITAN TAG // ALLEGIANCE</span>
        <h2>Pick your franchise.</h2>
        {alignedTeam ? (
          <p data-testid="franchise-picker-status">
            <ShieldCheck size={14} /> YOUR TAG FLIES <strong style={{ color: alignedTeam.color }}>{alignedTeam.name}</strong>
            {" — "}switching allegiance is free. Your crest shows on the runner roster.
          </p>
        ) : user ? (
          <p data-testid="franchise-picker-status">
            No allegiance yet, {String(user.name || "").toUpperCase()}. Choose a crest — it rides with your Titan Tag on
            the runner roster.
          </p>
        ) : (
          <p data-testid="franchise-picker-status">
            Ten franchises want your loyalty. <Link to="/account" data-testid="franchise-picker-signin">Sign in or enlist</Link>{" "}
            to align your Titan Tag — your crest will show beside your name on the runner roster.
          </p>
        )}
      </div>
      <div className="franchise-picker-grid">
        {riotballTeams.map((team) => {
          const isAligned = aligned === team.id;
          return (
            <button
              type="button"
              key={team.id}
              className={`franchise-pick${isAligned ? " franchise-pick-aligned" : ""}`}
              style={{ "--team-color": team.color }}
              onClick={() => pick(team)}
              disabled={saving !== null}
              data-testid={`franchise-pick-${team.id}`}
              aria-pressed={isAligned}
            >
              <img src={`/assets/team-logo-${team.id}.png`} alt={team.alt} loading="lazy" />
              <strong>{team.name}</strong>
              <span>{isAligned ? "ALIGNED" : saving === team.id ? "ALIGNING…" : team.tag}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
